
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FormattingToolbar } from "./FormattingToolbar";
import { formatText } from "./utils/textFormatting";
import { ProductFormValues, ProductFormProps } from "./types";
import { HtmlEditor } from "./HtmlEditor";

export const ProductForm = ({ onSuccess }: ProductFormProps) => {
  const { toast } = useToast();
  const form = useForm<ProductFormValues>({
    defaultValues: {
      name: "",
      description: "",
      features: "",
      link: "",
      logo: "",
      product_screen: "",
    },
  });

  const onSubmit = async (data: ProductFormValues) => {
    try {
      const { error } = await supabase.from("products").insert([data]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Product created successfully",
      });
      
      form.reset();
      onSuccess();
      window.location.reload();
    } catch (error) {
      console.error("Error creating product:", error);
      toast({
        title: "Error",
        description: "Failed to create product",
        variant: "destructive",
      });
    }
  };

  const handleFormat = (format: string, fieldName: 'features') => {
    const textarea = document.querySelector(`textarea[name="${fieldName}"]`) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    const formattedText = formatText(format, text, start, end);
    if (formattedText) {
      form.setValue(fieldName, formattedText);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Product name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <HtmlEditor name="description" placeholder="Product description" className="min-h-[150px]" />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="features"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Features</FormLabel>
              <div className="mb-2">
                <FormattingToolbar onFormat={(format) => handleFormat(format, 'features')} />
              </div>
              <FormControl>
                <Textarea 
                  placeholder="List the product features here..." 
                  className="min-h-[150px]" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Link</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com/product" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="logo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Logo URL</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com/logo.png" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="product_screen"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Screenshot URL</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com/screenshot.png" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full">Create Product</Button>
      </form>
    </Form>
  );
};
